/**
 * Saga wiring for the in-house dva replacement (ENG-4570).
 *
 * Ports dva-core's getSaga / createEffects / promise middleware onto a directly
 * controlled redux-saga@1.x. Effects keep the same call signature
 * `effect(action, { call, put, select, all, take, ... })`, the same default
 * `takeEvery` watcher per `namespace/effect`, and the same effect-internal
 * `put`/`take` namespace prefixing.
 */
// cspell:ignore dont
import createSagaMiddleware from 'redux-saga'
import * as sagaEffects from 'redux-saga/effects'
import { Middleware } from 'redux'
import { Model, NAMESPACE_SEP, prefixType } from './reducer'

export { createSagaMiddleware }

type OnError = (err: any, extension: { key: string; effectArgs: any[] }) => void
type OnEffect = (effect: any, effects: typeof sagaEffects, model: Model, key: string) => any

const noop = () => {}

/**
 * The effect helpers handed to each effect: the full redux-saga effects surface
 * with `put`/`take` overridden to apply namespace prefixing.
 */
const createEffects = (model: Model) => {
  const put: any = (action: any) =>
    sagaEffects.put({ ...action, type: prefixType(action.type, model) })
  put.resolve = (action: any) =>
    sagaEffects.putResolve({ ...action, type: prefixType(action.type, model) })

  const take = (type: any): any => {
    if (typeof type === 'string') {
      return sagaEffects.take(prefixType(type, model))
    }
    if (Array.isArray(type)) {
      return sagaEffects.take(type.map((t) => (typeof t === 'string' ? prefixType(t, model) : t)))
    }
    return sagaEffects.take(type)
  }

  return { ...sagaEffects, put, take }
}

const applyOnEffect = (fns: OnEffect[], effect: any, model: Model, key: string) =>
  fns.reduce((wrapped, fn) => fn(wrapped, sagaEffects, model, key), effect)

const getWatcher = (
  key: string,
  rawEffect: any,
  model: Model,
  onError: OnError,
  onEffect: OnEffect[],
) => {
  let effect = rawEffect
  let type = 'takeEvery'
  let ms: number | undefined

  if (Array.isArray(rawEffect)) {
    ;[effect] = rawEffect
    const opts = rawEffect[1]
    if (opts && opts.type) {
      ;({ type } = opts)
      if (type === 'throttle') {
        ms = opts.ms
      }
    }
  }

  function* sagaWithCatch(...args: any[]): any {
    const { __dva_resolve: resolve = noop, __dva_reject: reject = noop } =
      args.length > 0 ? args[0] : {}
    try {
      yield sagaEffects.put({ type: `${key}${NAMESPACE_SEP}@@start` })
      const ret = yield effect(...args.concat(createEffects(model)))
      yield sagaEffects.put({ type: `${key}${NAMESPACE_SEP}@@end` })
      resolve(ret)
    } catch (e: any) {
      onError(e, { key, effectArgs: args })
      if (!e._dontReject) {
        reject(e)
      }
    }
  }

  const sagaWithOnEffect = applyOnEffect(onEffect, sagaWithCatch, model, key)

  switch (type) {
    case 'watcher':
      return sagaWithCatch
    case 'takeLatest':
      return function* () {
        yield sagaEffects.takeLatest(key, sagaWithOnEffect)
      }
    case 'throttle':
      return function* () {
        yield sagaEffects.throttle(ms as number, key, sagaWithOnEffect)
      }
    default:
      return function* () {
        yield sagaEffects.takeEvery(key, sagaWithOnEffect)
      }
  }
}

/**
 * Build the root saga for a model: fork a watcher per effect, each cancellable
 * via `${namespace}/@@CANCEL_EFFECTS` (parity with dva-core; unused today).
 */
export const getSaga = (
  effects: Record<string, any>,
  model: Model,
  onError: OnError,
  onEffect: OnEffect[],
) => {
  return function* (): any {
    for (const key in effects) {
      if (Object.prototype.hasOwnProperty.call(effects, key)) {
        const watcher = getWatcher(key, effects[key], model, onError, onEffect)
        const task = yield sagaEffects.fork(watcher)
        yield sagaEffects.fork(function* () {
          yield sagaEffects.take(`${model.namespace}${NAMESPACE_SEP}@@CANCEL_EFFECTS`)
          yield sagaEffects.cancel(task)
        })
      }
    }
  }
}

/**
 * Dispatching a registered `namespace/effect` returns a promise that settles
 * when the effect completes, by injecting `__dva_resolve`/`__dva_reject` into
 * the action. Mirrors dva-core's promise middleware.
 */
export const createPromiseMiddleware = (app: { _models: Model[] }): Middleware => {
  const isEffect = (type: any) => {
    if (!type || typeof type !== 'string') {
      return false
    }
    const [namespace] = type.split(NAMESPACE_SEP)
    const model = app._models.find((m) => m.namespace === namespace)
    const effects = model?.effects
    return Boolean(effects && effects[type])
  }

  return () => (next) => (action: any) => {
    if (isEffect(action.type)) {
      return new Promise((resolve, reject) => {
        // Spread action first so the injected callbacks always win, even if an
        // action somehow already carries these internal keys.
        next({ ...action, __dva_resolve: resolve, __dva_reject: reject })
      })
    }
    return next(action)
  }
}
